import React, { useState } from 'react';
import { toast } from 'sonner';
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogFooter } from '../../components/ui/dialog';
import { Button } from '../../components/ui/button';
import { Input } from '../../components/ui/input';
import { Label } from '../../components/ui/label';
import { Textarea } from '../../components/ui/textarea';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '../../components/ui/select';

export default function NewApplicationDialog({ open, onOpenChange, jobs, refData, apiCall, onCreated }) {
  const [form, setForm] = useState({
    job_id: '', candidate_name: '', candidate_email: '',
    expected_salary_min: '', expected_salary_max: '', expected_salary_currency: 'USD',
    notice_period_text: '', source_type: 'manual', department_name: '',
    primary_manager_person_id: '', secondary_manager_person_id: '', notes: '',
  });
  const [saving, setSaving] = useState(false);
  const set = (k, v) => setForm(p => ({ ...p, [k]: v }));

  const handleJobChange = (jobId) => {
    set('job_id', jobId);
    const job = jobs.find(j => j.job_id === jobId);
    if (job) {
      set('department_name', job.department_name || '');
      set('primary_manager_person_id', job.primary_manager_person_id || '');
      set('secondary_manager_person_id', job.secondary_manager_person_id || '');
    }
  };

  const handleSave = async () => {
    if (!form.job_id) { toast.error('Select a job'); return; }
    if (!form.candidate_name.trim() && !form.candidate_email.trim()) {
      toast.error('Name or email required'); return;
    }
    setSaving(true);
    try {
      await apiCall('POST', '/recruitment/applications', {
        ...form,
        expected_salary_min: form.expected_salary_min ? Number(form.expected_salary_min) : null,
        expected_salary_max: form.expected_salary_max ? Number(form.expected_salary_max) : null,
      });
      toast.success('Application created');
      onCreated();
      setForm({ job_id: '', candidate_name: '', candidate_email: '', expected_salary_min: '',
        expected_salary_max: '', expected_salary_currency: 'USD', notice_period_text: '',
        source_type: 'manual', department_name: '', primary_manager_person_id: '',
        secondary_manager_person_id: '', notes: '' });
    } catch (e) { toast.error(e.message); }
    setSaving(false);
  };

  return (
    <Dialog open={open} onOpenChange={onOpenChange}>
      <DialogContent className="max-w-lg">
        <DialogHeader>
          <DialogTitle>Add Applicant</DialogTitle>
        </DialogHeader>
        <div className="grid gap-4 py-2">
          <div>
            <Label>Job *</Label>
            <Select value={form.job_id || '_none'} onValueChange={v => handleJobChange(v === '_none' ? '' : v)}>
              <SelectTrigger data-testid="new-app-job-select"><SelectValue placeholder="Select job..." /></SelectTrigger>
              <SelectContent>
                <SelectItem value="_none" disabled>Select job...</SelectItem>
                {jobs.map(j => <SelectItem key={j.job_id} value={j.job_id}>{j.title}</SelectItem>)}
              </SelectContent>
            </Select>
          </div>
          <div className="grid grid-cols-2 gap-3">
            <div>
              <Label>Candidate Name</Label>
              <Input value={form.candidate_name} onChange={e => set('candidate_name', e.target.value)}
                placeholder="Ada Lovelace" data-testid="new-app-name-input" />
            </div>
            <div>
              <Label>Email</Label>
              <Input value={form.candidate_email} onChange={e => set('candidate_email', e.target.value)}
                placeholder="ada@example.com" data-testid="new-app-email-input" />
            </div>
          </div>
          <div className="grid grid-cols-3 gap-3">
            <div>
              <Label>Salary Min</Label>
              <Input type="number" value={form.expected_salary_min} onChange={e => set('expected_salary_min', e.target.value)} />
            </div>
            <div>
              <Label>Salary Max</Label>
              <Input type="number" value={form.expected_salary_max} onChange={e => set('expected_salary_max', e.target.value)} />
            </div>
            <div>
              <Label>Currency</Label>
              <Input value={form.expected_salary_currency} onChange={e => set('expected_salary_currency', e.target.value)} />
            </div>
          </div>
          <div className="grid grid-cols-2 gap-3">
            <div>
              <Label>Notice Period</Label>
              <Input value={form.notice_period_text} onChange={e => set('notice_period_text', e.target.value)} placeholder="e.g. 2 weeks" />
            </div>
            <div>
              <Label>Source</Label>
              <Select value={form.source_type} onValueChange={v => set('source_type', v)}>
                <SelectTrigger><SelectValue /></SelectTrigger>
                <SelectContent>
                  <SelectItem value="manual">Manual</SelectItem>
                  <SelectItem value="linkedin">LinkedIn</SelectItem>
                  <SelectItem value="referral">Referral</SelectItem>
                  <SelectItem value="careers_page">Careers Page</SelectItem>
                  <SelectItem value="agency">Agency</SelectItem>
                </SelectContent>
              </Select>
            </div>
          </div>
          <div className="grid grid-cols-2 gap-3">
            <div>
              <Label>Primary Manager</Label>
              <Select value={form.primary_manager_person_id || '_none'} onValueChange={v => set('primary_manager_person_id', v === '_none' ? '' : v)}>
                <SelectTrigger><SelectValue placeholder="Select..." /></SelectTrigger>
                <SelectContent>
                  <SelectItem value="_none">None</SelectItem>
                  {(refData?.members || []).map(m => <SelectItem key={m.person_id} value={m.person_id}>{m.name}</SelectItem>)}
                </SelectContent>
              </Select>
            </div>
            <div>
              <Label>Secondary Manager</Label>
              <Select value={form.secondary_manager_person_id || '_none'} onValueChange={v => set('secondary_manager_person_id', v === '_none' ? '' : v)}>
                <SelectTrigger><SelectValue placeholder="Select..." /></SelectTrigger>
                <SelectContent>
                  <SelectItem value="_none">None</SelectItem>
                  {(refData?.members || []).map(m => <SelectItem key={m.person_id} value={m.person_id}>{m.name}</SelectItem>)}
                </SelectContent>
              </Select>
            </div>
          </div>
          <div>
            <Label>Notes</Label>
            <Textarea value={form.notes} onChange={e => set('notes', e.target.value)} rows={2} />
          </div>
        </div>
        <DialogFooter>
          <Button variant="outline" onClick={() => onOpenChange(false)}>Cancel</Button>
          <Button onClick={handleSave} disabled={saving} data-testid="save-applicant-btn">
            {saving ? 'Saving...' : 'Add Applicant'}
          </Button>
        </DialogFooter>
      </DialogContent>
    </Dialog>
  );
}
